import { ColorNameOrHex, nameToHexOrInherit, negativeBlackOrWhiteHex } from "@cervval.js/common/src/tools/color.tools";

/**
 * Describe all fontawesome classes for styling icon
 * @see https://fontawesome.com/v6/docs/web/add-icons/how-to
 * 
 * ! Use of "fa/fas/far/fal/fab/fat" class is deprecated (but still supported in 06/22) !
 */
// TODO move to "iconFA.tools"
export enum FaClasses {
    SOLID = "fa-solid",
    REGULAR = "fa-regular",
    LIGHT = "fa-light",
    DUOTONE = "fa-duotone",
    BRAND = "fa-brands",
    THIN = "fa-thin"
}
// cssClasses.tools
// Display
export enum Display {
    BLOCK = "block",
    INLINE_BLOCK = "inline-block",
    INLINE_FLEX = "inline-flex",
    NONE = "none",
    FLEX = "flex",
    INITIAL = "initial",
    INHERIT = "inherit",
    INLINE = "inline",
    LIST_ITEM = "list-item",
    TABLE_ROW = "table-row",
    TABLE_ROW_GROUP = "table-row-group",
    TABLE_FOOTER_GROUP = "table-footer-group",
    TABLE_HEADER_GROUP = "table-header-group",
    GRID = "grid"
}

/**
 * Set element display to visibleDisplay or none depending on visible value
 */
export function toggleVisible(element: HTMLElement, visible: boolean | undefined, visibleDisplay?: Display): void {
    if (!visible) { element.style.display = Display.NONE; return; }
    if (visibleDisplay === undefined) { element.style.removeProperty("display"); return; }
    element.style.display = visibleDisplay;
}

/**
 * Set element display to display value
 */
export function setDisplay(element: HTMLElement, display: Display): void {
    element.style.display = display;
}

/**
 * Add or remove css class from element depending on classPresent value
 */
export function toggleClass(element: HTMLElement, classPresent: boolean | undefined, cssClass: string): void {
    if (classPresent) {
        element.classList.add(cssClass);
    } else {
        element.classList.remove(cssClass);
    }
}

export interface ColorStyleOptions {
    text?: ColorNameOrHex;
    background?: ColorNameOrHex;
    border?: ColorNameOrHex;
}

export class ColorStyle implements ColorStyleOptions {
    public text?: ColorNameOrHex;
    public background?: ColorNameOrHex;
    public border?: ColorNameOrHex;

    constructor(colorStyleOptions?: ColorStyleOptions) {
        Object.assign(this, colorStyleOptions);
    }

    /** 
     * Apply color, background-color and border-color, if defined. 
     * If background is defined, and not color it will use {@link negativeBlackOrWhiteHex} 
     */
    public applyTo(el: HTMLElement | undefined): void {
        if (!el) { return; }

        if (this.border) {
            el.style.borderColor = this.border;
            // Bootstrap5 border color
            el.style.setProperty("--bs-border-color", this.border);
        } else {
            el.style.borderColor = "";
        }
        if (this.text) { el.style.color = this.text; }
        if (this.background) {
            el.style.backgroundColor = this.background;
            if (!this.text) {
                const hexOrInherit = nameToHexOrInherit(this.background);
                const negativeColor = negativeBlackOrWhiteHex(hexOrInherit);
                if (negativeColor != null) {
                    el.style.color = negativeColor;
                }
            }
        }
    }

    /** 
     * UnApply color, background-color and border-color, if defined in ColorStyle
     */
    public unApplyTo(el: HTMLElement | undefined): void {
        if (!el) { return; }

        if (this.border) {
            el.style.borderColor = "";
        }

        if (this.background) {
            el.style.backgroundColor = "";
        }

        if (this.text) {
            el.style.color = "";
        }
    }

    /** use {@link ColorStyle.prototype.applyTo} with a {@link ColorStyleOptions}, use it for one time style */
    public static applyTo(el: HTMLElement, colors: ColorStyleOptions): void {
        new ColorStyle(colors)?.applyTo(el);
    }
}